import cn from 'classnames';
import React, { useEffect, useState } from 'react';

const PageNavigation = ({ pages, currentPage, isInPage, pathname }) => {
  const maxPageOffset = pages.length - 4;

  const [pageNavigationOffset, setPageNavigationOffset] = useState(0);
  const [hideLessOffset, setHideLessOffset] = useState(false);
  const [hideMoreOffset, setHideMoreOffset] = useState(false);
  const [direction, setDirection] = useState('right');

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    if (isInPage && !isNaN(currentPage) && !loaded) {
      if (currentPage <= 2) {
        setPageNavigationOffset(0);
      } else if (currentPage > maxPageOffset) {
        setPageNavigationOffset(maxPageOffset);
      } else {
        setPageNavigationOffset(currentPage - 2);
      }
      setLoaded(true);
    }
  }, [isInPage, currentPage, pages, loaded]);

  function getHref(n) {
    return `/${pathname}${n === 0 ? '' : `?page=${n + 1}`}`;
  }

  function handleLessOffset() {
    setPageNavigationOffset(pageNavigationOffset - 1);
    setDirection('left');
  }

  function handleMoreOffset() {
    setPageNavigationOffset(pageNavigationOffset + 1);
    setDirection('right');
  }

  useEffect(() => {
    if (pageNavigationOffset >= maxPageOffset) setHideMoreOffset(true);
    else setHideMoreOffset(false);
    if (pageNavigationOffset === 0) setHideLessOffset(true);
    else setHideLessOffset(false);
  }, [pageNavigationOffset, pages]);

  return (
    <div className="flex flex-col items-center sm:flex-row">
      <a
        aria-label="Previous Page"
        href={currentPage !== 0 ? getHref(currentPage - 1) : null}
        className={cn(
          `flex items-center justify-center text-sm text-gray-900 font-medium font-inter mb-3 mr-0 sm:mb-0 sm:mr-3`,
          currentPage === 0
            ? 'hidden cursor-not-allowed'
            : 'hover:text-blue-100 cursor-pointer',
        )}
      >
        <span className="hidden sm:inline-block">{'<-'}&nbsp;</span>
        Previous
      </a>
      <div className="flex">
        {!hideLessOffset && (
          <button
            aria-label="Hide Less"
            className="text-gray-900 font-medium font-inter mx-3 w-5 hover:text-blue-100"
            onClick={handleLessOffset}
          >
            ...
          </button>
        )}
        {pages?.map((_page, i) => {
          if (i < pageNavigationOffset) return null;
          if (i > pageNavigationOffset + 3) return null;
          if (!hideLessOffset && !hideMoreOffset) {
            if (direction === 'left' && i > pageNavigationOffset + 2) {
              return null;
            }
            if (direction === 'right' && i < pageNavigationOffset + 1) {
              return null;
            }
          }
          return (
            <a
              aria-label={`To page ${i}`}
              href={currentPage !== i ? getHref(i) : null}
              key={`to-page-${i}`}
              className={cn(
                `flex items-center justify-center shadow-header border border-gray-100 mx-3 font-medium font-inter w-12 h-12 rounded-md`,
                currentPage === i
                  ? 'text-black cursor-not-allowed'
                  : 'hover:text-blue-100 text-gray-900 cursor-pointer',
              )}
            >
              {i + 1}
            </a>
          );
        })}
        {!hideMoreOffset && (
          <button
            className="text-gray-900 font-medium font-inter mx-3 w-5 hover:text-blue-100"
            onClick={handleMoreOffset}
            aria-label="Ellipsis"
          >
            ...
          </button>
        )}
      </div>
      <a
        href={
          currentPage + 1 !== pages.length ? getHref(currentPage + 1) : null
        }
        className={cn(
          `flex items-center justify-center text-sm font-medium text-gray-900 font-inter mt-3 ml-0 sm:mt-0 sm:ml-3`,
          currentPage + 1 === pages.length
            ? 'hidden cursor-not-allowed'
            : 'cursor-pointer hover:text-blue-100',
        )}
        aria-label="Next Page"
      >
        Next<span className="hidden sm:inline-block">&nbsp;{'->'}</span>
      </a>
    </div>
  );
};

export default PageNavigation;
